/**
 * Real-time message stream for a single conversation.
 *
 * Subscribes to `users/{uid}/conversations/{conversationId}/messages` ordered by
 * `createdAt` ascending. Because the backend streams the assistant reply by
 * mutating a `status:'streaming'` message document in place, `onSnapshot`
 * surfaces the partial content live — no SSE needed.
 */
import type { MessageDoc } from '@agente/shared'
import { onSnapshot, orderBy, query } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { messagesCol } from '@/lib/firestore'

export interface UseMessagesResult {
  messages: MessageDoc[]
  loading: boolean
  error: string | null
}

export function useMessages(conversationId: string | null): UseMessagesResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [messages, setMessages] = useState<MessageDoc[]>([])
  const [loading, setLoading] = useState(Boolean(conversationId))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid || !conversationId) {
      setMessages([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    const q = query(messagesCol(uid, conversationId), orderBy('createdAt', 'asc'))
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => ({ ...d.data(), id: d.id })))
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid, conversationId])

  return { messages, loading, error }
}
